import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge'
import { hpscoutsoRemoteReducers } from 'app/constants/remoteReducers'
import { AppLoaderWrapper } from 'app/providers/AppLoaderWrapper'
import { CustomRoutes } from 'app/routes/CustomRoutes'
import { reducer, setApplicationStore } from 'app/store/index'
import { WidgetSelector } from 'app/widgets/WidgetSelector'
import { calendarReducer } from 'legacy/hpscoutso/features/activityCalendar/calendarSlice'
import { filterReducer } from 'legacy/hpscoutso/features/filterContent/filterSlice'
import { middlewares } from 'legacy/lut/store/middlewares'
import { createRoot } from 'react-dom/client'

import { Roles } from 'clickNgo/configs/roles'
import { App } from 'core/components/App'
import 'core/utils/Logger'
import { lazyWidgets } from 'src/config/lazyWidgets'

const container = document.getElementById('root') as HTMLElement
const root = createRoot(container)

const headerSettings = {
  editModeAccessRoles: [
    Roles.ADMIN,
    Roles.SPV_IPBM,
    Roles.SPV_TR,
    Roles.HP_MGMT,
    Roles.TRANSACTIONAL_MANAGER,
    Roles.SC_ADMIN,
  ],
}

const localStorageSettings = {
  preservedKeys: [
    '__partner360-initiatives-and-actions-default-view',
    '__partner360-planning-keep-old-view',
    '__partner360-planning-keep-new-view',
    '__partner360-planning-promo-spotlight-shown',
    '__partner360-financial-summary-planning-activities-seen',
    // One exact key per widget tour, matching getSeenStorageKey(tour.id); a changed tour id
    // needs a changed key here or the tour auto-starts again after every login.
    '__partner360-planning-cockpit-overview-seen',
    '__partner360-manager-cockpit-overview-seen',
  ],
}

const MSAL_POPUP_REDIRECT_PATH = '/msal-popup-redirect'
const MSAL_POPUP_COMPLETED_MESSAGE_TYPE = 'msal-popup-redirect-completed'
const MSAL_POPUP_COMPLETED_STORAGE_KEY = 'msal-popup-redirect-completed'

const hasAuthResponseInUrl = () => {
  const hash = window.location.hash
  const search = window.location.search

  return (
    hash.includes('code=') ||
    search.includes('code=') ||
    hash.includes('error=') ||
    search.includes('error=')
  )
}

const isMsalPopupRedirectPath = () => {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/'
  return normalizedPath === MSAL_POPUP_REDIRECT_PATH
}

const notifyMsalPopupCompleted = () => {
  const payload = {
    type: MSAL_POPUP_COMPLETED_MESSAGE_TYPE,
    timestamp: Date.now(),
  }

  try {
    window.localStorage.setItem(MSAL_POPUP_COMPLETED_STORAGE_KEY, `${payload.timestamp}`)
  } catch {
    // Ignore storage errors, popup can still notify opener via postMessage.
  }

  if (window.opener && window.opener !== window) {
    window.opener.postMessage(payload, window.location.origin)
  }
}

const handleMsalPopupRedirect = async () => {
  if (!isMsalPopupRedirectPath() || !hasAuthResponseInUrl()) {
    return false
  }

  try {
    await broadcastResponseToMainFrame()
  } catch {
    // If redirect-bridge processing fails, still attempt to close popup
  } finally {
    notifyMsalPopupCompleted()
    window.close()
  }

  return true
}

const initApp = async () => {
  if (await handleMsalPopupRedirect()) {
    return
  }

  root.render(
    <App
      appStore={{
        remoteReducers: hpscoutsoRemoteReducers,
        storeReducer: {
          ...reducer,
          activityCalendar: calendarReducer,
          filtersContent: filterReducer,
        },
        storeMiddleware: middlewares,
        setApplicationStore,
        devTools: {
          serialize: true,
        },
      }}
      AppLazyWidgets={lazyWidgets}
      AppWidgets={WidgetSelector}
      CustomRoutes={CustomRoutes}
      LoaderWrapper={AppLoaderWrapper}
      headerSettings={headerSettings}
      localStorageSettings={localStorageSettings}
    />
  )
}

initApp()
